/**
 * Rubric Studio - Main Workspace
 * Features: Visual/JSON views, AI Chat sidebar, synchronized state
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  Breadcrumbs,
  Link as MuiLink,
  AppBar,
  Toolbar,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CircularProgress,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Stack,
  Divider,
  Chip,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import SaveIcon from '@mui/icons-material/Save';
import DownloadIcon from '@mui/icons-material/Download';
import ScienceIcon from '@mui/icons-material/Science';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import CodeIcon from '@mui/icons-material/Code';
import SendIcon from '@mui/icons-material/Send';
import AddIcon from '@mui/icons-material/Add';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import InfoIcon from '@mui/icons-material/Info';
import markOtterImage from '../../assets/images/mark-otter.png';
import { RUBRIC_GUIDE_TYPES, RubricGuideType, getRecommendedGuideType } from '../../constants/rubricGuideTypes';

interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
}

interface Criterion {
  name: string;
  description: string;
  max_points: number;
  levels?: any[];
  [key: string]: any; // Allow any additional fields from AI-generated rubrics
}

interface Rubric {
  id?: number;
  name: string;
  description: string;
  criteria: Record<string, Criterion>;
  marking_preamble?: string;  // Used when AI marks submissions (NOT for rubric generation)
  marking_postamble?: string;  // Used when AI marks submissions (NOT for rubric generation)
  source_type?: string;
  source_question_paper?: string | null;
  source_memo?: string | null;
  source_description?: string | null;
}

// Isolated chat input — owns its own message state so typing never triggers
// a re-render of the expensive workspace panel above it.
const ChatInput: React.FC<{ onSend: (msg: string) => void; disabled: boolean }> = React.memo(({ onSend, disabled }) => {
  const [message, setMessage] = useState('');

  const handleSend = () => {
    if (message.trim()) {
      onSend(message.trim());
      setMessage('');
    }
  };

  return (
    <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
      <Stack direction="row" spacing={1}>
        <TextField
          fullWidth
          size="small"
          placeholder="Type your message..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          disabled={disabled}
          multiline
          rows={3}
        />
        <IconButton color="primary" onClick={handleSend} disabled={!message.trim() || disabled}>
          {disabled ? <CircularProgress size={24} /> : <SendIcon />}
        </IconButton>
      </Stack>
    </Box>
  );
});

const RubricStudio: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode'); // create, refine, import
  const rubricId = searchParams.get('rubricId');

  // Core state
  const [rubric, setRubric] = useState<Rubric | null>(null);
  const [viewMode, setViewMode] = useState<'visual' | 'json'>('visual');
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [isSendingChat, setIsSendingChat] = useState(false);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  // JSON editor state
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showRubricSelector, setShowRubricSelector] = useState(false);
  const [availableRubrics, setAvailableRubrics] = useState<any[]>([]);
  const [isTesting, setIsTesting] = useState(false);
  const [testSample, setTestSample] = useState('');
  const [testResults, setTestResults] = useState<any>(null);

  // Create New workflow state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createMethod, setCreateMethod] = useState<'upload' | 'paste' | 'describe'>('upload');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [questionPaper, setQuestionPaper] = useState('');
  const [memorandum, setMemorandum] = useState('');
  const [rubricName, setRubricName] = useState('');
  const [rubricDescription, setRubricDescription] = useState('');
  const [selectedGuideType, setSelectedGuideType] = useState<string>(getRecommendedGuideType().value);
  const [showGuideTypesModal, setShowGuideTypesModal] = useState(false);
  const [exampleRubric, setExampleRubric] = useState<string>('');
  const [additionalInstructions, setAdditionalInstructions] = useState('');

  // Import JSON workflow state
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importJsonText, setImportJsonText] = useState('');
  const [importJsonError, setImportJsonError] = useState<string | null>(null);

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Sync JSON text when rubric changes
  useEffect(() => {
    if (rubric) {
      setJsonText(JSON.stringify(rubric.criteria, null, 2));
      setJsonError(null);
    }
  }, [rubric]);

  // Load example rubric when guide type changes
  useEffect(() => {
    const guideType = RUBRIC_GUIDE_TYPES.find(type => type.value === selectedGuideType);
    if (guideType?.exampleRubric) {
      setExampleRubric(guideType.exampleRubric);
    } else {
      setExampleRubric('');
    }
  }, [selectedGuideType]);

  // Initialize based on mode
  useEffect(() => {
    const initialize = async () => {
      if (mode === 'refine') {
        if (rubricId) {
          // Load specific rubric
          await loadRubric(parseInt(rubricId));
        } else {
          // Show rubric selector
          await fetchAvailableRubrics();
          setShowRubricSelector(true);
        }
      } else if (mode === 'import') {
        // Show import dialog
        setShowImportDialog(true);
      } else if (mode === 'create') {
        // Show creation dialog
        setShowCreateDialog(true);
      }
    };
    initialize();
  }, [mode, rubricId]);

  const fetchAvailableRubrics = async () => {
    try {
      const response = await axios.get('/api/rubrics/');
      setAvailableRubrics(response.data.results || response.data);
    } catch (err) {
      console.error('Error fetching rubrics:', err);
      setError('Failed to load rubrics');
    }
  };

  const loadRubric = async (id: number) => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/rubrics/${id}/`);

      // Log the raw data to understand structure
      console.log('Raw rubric data:', response.data);
      console.log('Criteria:', response.data.criteria);

      // Load criteria AS-IS without modification to preserve all fields
      // Only add missing required fields if they don't exist
      const loadedCriteria: Record<string, Criterion> = {};
      Object.entries(response.data.criteria).forEach(([key, value]: [string, any]) => {
        loadedCriteria[key] = {
          ...value, // Keep ALL original fields
          // Only set defaults for truly missing required fields
          name: value.name || value.criterion || key,
          description: value.description || '',
          max_points: value.max_points || value.maxPoints || value.max_score || 0,
        };
      });

      const loadedRubric: Rubric = {
        id: response.data.id,
        name: response.data.name,
        description: response.data.description || '',
        criteria: loadedCriteria,
        marking_preamble: response.data.marking_preamble || '',
        marking_postamble: response.data.marking_postamble || '',
        source_type: response.data.source_type,
        source_question_paper: response.data.source_question_paper,
        source_memo: response.data.source_memo,
        source_description: response.data.source_description,
      };

      console.log('Loaded rubric (preserving all fields):', loadedRubric);
      setRubric(loadedRubric);

      // Add welcome message
      const welcomeMsg: ChatMessage = {
        role: 'assistant',
        content: `Hello! I've loaded "${loadedRubric.name}". I'm here to help you refine it. You can:\n\n• Modify criteria (names, descriptions, points)\n• Add or remove criteria\n• Restructure the rubric\n• Update preamble and postamble prompts\n\nWhat would you like to change?`,
        timestamp: new Date(),
      };
      setChatMessages([welcomeMsg]);
      setSuccess(`Loaded: ${loadedRubric.name}`);
    } catch (err: any) {
      console.error('Error loading rubric:', err);
      setError('Failed to load rubric');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (messageText: string) => {
    const text = (messageText ?? '').trim();
    if (!text || !rubric) return;

    const userMsg: ChatMessage = {
      role: 'user',
      content: text,
      timestamp: new Date(),
    };

    setChatMessages(prev => [...prev, userMsg]);
    setIsSendingChat(true);
    setError(null);

    try {
      const response = await axios.post('/api/rubrics/ai-refine/', {
        criteria: rubric.criteria,
        feedback: text,
        marking_preamble: rubric.marking_preamble,
        marking_postamble: rubric.marking_postamble,
        conversation_history: chatMessages.map(msg => ({
          role: msg.role,
          content: msg.content,
        })),
      });

      // Update rubric (including preamble/postamble if they were modified)
      setRubric(prev => prev ? {
        ...prev,
        criteria: response.data.refined_criteria || prev.criteria,
        marking_preamble: response.data.marking_preamble || prev.marking_preamble,
        marking_postamble: response.data.marking_postamble || prev.marking_postamble,
      } : null);

      // Add AI response
      const assistantMsg: ChatMessage = {
        role: 'assistant',
        content: response.data.message || 'Rubric updated successfully!',
        timestamp: new Date(),
      };
      setChatMessages(prev => [...prev, assistantMsg]);

    } catch (err: any) {
      console.error('Error:', err);
      const errorMsg: ChatMessage = {
        role: 'system',
        content: err.response?.data?.error || 'Failed to process request',
        timestamp: new Date(),
      };
      setChatMessages(prev => [...prev, errorMsg]);
    } finally {
      setIsSendingChat(false);
    }
  };

  const handleJSONChange = (newJSON: string) => {
    setJsonText(newJSON);
    try {
      const parsed = JSON.parse(newJSON);
      setJsonError(null);
      // Update rubric state
      if (rubric) {
        setRubric({
          ...rubric,
          criteria: parsed,
        });
      }
    } catch (err: any) {
      setJsonError(err.message);
    }
  };

  const handleCreateFromUpload = async () => {
    if (!rubricName.trim()) {
      setError('Please provide a rubric name');
      return;
    }
    if (!uploadedFile) {
      setError('Please upload a file');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', uploadedFile);
      formData.append('assignment_type', 'general');
      formData.append('guide_type', selectedGuideType);
      if (exampleRubric) {
        formData.append('example_rubric', exampleRubric);
      }
      if (additionalInstructions) {
        formData.append('additional_instructions', additionalInstructions);
      }

      const response = await axios.post('/api/rubrics/ai-analyze/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      console.log('AI Analyze Response:', response.data);

      if (!response.data.criteria || Object.keys(response.data.criteria).length === 0) {
        setError('Failed to extract rubric criteria from file. Please check the file format.');
        setLoading(false);
        return;
      }

      const newRubric: Rubric = {
        name: rubricName || response.data.rubric_name || 'Untitled Rubric',
        description: rubricDescription,
        criteria: response.data.criteria,
        marking_preamble: response.data.marking_preamble || '',
        marking_postamble: response.data.marking_postamble || '',
        source_type: response.data.metadata?.source_type || 'upload',
        source_question_paper: response.data.metadata?.source_question_paper || null,
        source_memo: response.data.metadata?.source_memo || null,
      };

      setRubric(newRubric);
      setShowCreateDialog(false);
      setSuccess('Rubric created successfully! Start refining with AI chat.');

      // Add welcome message
      const welcomeMsg: ChatMessage = {
        role: 'assistant',
        content: `I've created a rubric from your uploaded file. You can now refine it by chatting with me. Try asking to:\n\n• Adjust criterion weights\n• Add or remove criteria\n• Clarify descriptions\n• Restructure the rubric\n\nWhat would you like to change?`,
        timestamp: new Date(),
      };
      setChatMessages([welcomeMsg]);

    } catch (err: any) {
      console.error('Error creating rubric:', err);
      setError(err.response?.data?.error || 'Failed to create rubric from file');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFromPaste = async () => {
    if (!questionPaper.trim() || !memorandum.trim()) {
      setError('Please provide both question paper and memorandum');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post('/api/rubrics/ai-analyze/', {
        question_paper: questionPaper,
        memorandum: memorandum,
        assessment_type: 'general',
        guide_type: selectedGuideType,
        example_rubric: exampleRubric || undefined,
        additional_instructions: additionalInstructions || undefined,
      });

      const newRubric: Rubric = {
        name: rubricName || response.data.rubric_name || 'Untitled Rubric',
        description: rubricDescription,
        criteria: response.data.criteria,
        marking_preamble: response.data.marking_preamble || '',
        marking_postamble: response.data.marking_postamble || '',
        source_type: 'paste',
        source_question_paper: questionPaper,
        source_memo: memorandum,
        source_description: rubricDescription || null,
      };

      setRubric(newRubric);
      setShowCreateDialog(false);
      setSuccess('Rubric created successfully!');

      // Add welcome message
      const welcomeMsg: ChatMessage = {
        role: 'assistant',
        content: `I've created a rubric from your documents. Feel free to refine it using the chat. What would you like to adjust?`,
        timestamp: new Date(),
      };
      setChatMessages([welcomeMsg]);

    } catch (err: any) {
      console.error('Error creating rubric:', err);
      setError(err.response?.data?.error || 'Failed to create rubric');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFromDescription = async () => {
    if (!rubricDescription.trim()) {
      setError('Please provide a description of your rubric requirements');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post('/api/rubrics/ai-analyze/', {
        description: rubricDescription,
      });

      const newRubric: Rubric = {
        name: rubricName || response.data.rubric_name || 'Untitled Rubric',
        description: rubricDescription,
        criteria: response.data.criteria,
        source_type: 'description',
        source_description: rubricDescription,
      };

      setRubric(newRubric);
      setShowCreateDialog(false);
      setSuccess('Rubric created successfully!');

      // Add welcome message
      const welcomeMsg: ChatMessage = {
        role: 'assistant',
        content: `I've created a rubric based on your description. Let's refine it together! What changes would you like to make?`,
        timestamp: new Date(),
      };
      setChatMessages([welcomeMsg]);

    } catch (err: any) {
      console.error('Error creating rubric:', err);
      setError(err.response?.data?.error || 'Failed to create rubric');
    } finally {
      setLoading(false);
    }
  };

  const handleImportJson = () => {
    if (!importJsonText.trim()) {
      setImportJsonError('Please paste your JSON');
      return;
    }

    try {
      const parsed = JSON.parse(importJsonText);

      // Validate structure
      if (!parsed || typeof parsed !== 'object') {
        throw new Error('Invalid JSON structure');
      }

      const newRubric: Rubric = {
        name: 'Imported Rubric',
        description: '',
        criteria: parsed,
      };

      setRubric(newRubric);
      setShowImportDialog(false);
      setImportJsonError(null);
      setSuccess('Rubric imported successfully!');

      // Add welcome message
      const welcomeMsg: ChatMessage = {
        role: 'assistant',
        content: `I've loaded your rubric from JSON. You can now refine it using the chat or edit it directly in Visual/JSON view. What would you like to do?`,
        timestamp: new Date(),
      };
      setChatMessages([welcomeMsg]);

    } catch (err: any) {
      setImportJsonError(err.message || 'Invalid JSON format');
    }
  };

  const handleAddCriterion = () => {
    if (!rubric) return;

    const newKey = `criterion_${Object.keys(rubric.criteria).length + 1}`;
    const newCriterion: Criterion = {
      name: 'New Criterion',
      description: 'Description of the criterion',
      max_points: 10,
    };

    setRubric({
      ...rubric,
      criteria: {
        ...rubric.criteria,
        [newKey]: newCriterion,
      },
    });

    setSuccess('New criterion added!');
  };

  const handleAIEnhance = async () => {
    if (!rubric) return;

    setIsEnhancing(true);
    setError(null);

    try {
      const response = await axios.post('/api/rubrics/ai-refine/', {
        criteria: rubric.criteria,
        feedback: 'Please review and enhance this rubric. Improve clarity, balance point distribution, and add any missing important criteria.',
      });

      setRubric(prev => prev ? {
        ...prev,
        criteria: response.data.refined_criteria,
      } : null);

      const assistantMsg: ChatMessage = {
        role: 'assistant',
        content: response.data.message || 'I\'ve enhanced your rubric with improvements to clarity, balance, and completeness. Review the changes and let me know if you\'d like further adjustments!',
        timestamp: new Date(),
      };
      setChatMessages(prev => [...prev, assistantMsg]);
      setSuccess('Rubric enhanced successfully!');

    } catch (err: any) {
      console.error('Error enhancing rubric:', err);
      setError(err.response?.data?.error || 'Failed to enhance rubric');
    } finally {
      setIsEnhancing(false);
    }
  };

  const handleSave = async () => {
    if (!rubric) return;

    setLoading(true);
    try {
      if (rubric.id) {
        // Update existing
        await axios.put(`/api/rubrics/${rubric.id}/`, {
          name: rubric.name,
          description: rubric.description,
          criteria: rubric.criteria,
          marking_preamble: rubric.marking_preamble,
          marking_postamble: rubric.marking_postamble,
        });
        setSuccess('Rubric updated successfully!');
      } else {
        // Create new
        const response = await axios.post('/api/rubrics/', {
          name: rubric.name,
          description: rubric.description,
          criteria: rubric.criteria,
          marking_preamble: rubric.marking_preamble,
          marking_postamble: rubric.marking_postamble,
          is_fundamental: false,
          is_active: true,
        });
        setRubric({ ...rubric, id: response.data.id });
        setSuccess('Rubric saved successfully!');
      }
    } catch (err: any) {
      console.error('Error saving:', err);
      setError('Failed to save rubric');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (!rubric) return;

    const dataStr = JSON.stringify(rubric.criteria, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${rubric.name.replace(/\s+/g, '_')}_rubric.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleTest = async () => {
    if (!rubric || !testSample.trim()) return;

    setLoading(true);
    try {
      // Call marking simulation API (reuse existing endpoint)
      const response = await axios.post('/api/submissions/simulate-marking/', {
        rubric_criteria: rubric.criteria,
        sample_work: testSample,
      });
      setTestResults(response.data);
      setSuccess('Test completed!');
    } catch (err: any) {
      console.error('Error testing:', err);
      setError('Failed to test rubric');
    } finally {
      setLoading(false);
    }
  };

  const renderWorkspace = () => {
    if (isTesting) {
      return (
        <Box>
          <Typography variant="h6" gutterBottom>Test Rubric</Typography>
          <TextField
            fullWidth
            multiline
            rows={10}
            label="Sample Student Work"
            placeholder="Paste sample student work here..."
            value={testSample}
            onChange={(e) => setTestSample(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
            <Button
              variant="contained"
              onClick={handleTest}
              disabled={!testSample.trim() || loading}
              startIcon={loading ? <CircularProgress size={20} /> : <ScienceIcon />}
            >
              Run Test
            </Button>
            <Button variant="outlined" onClick={() => setIsTesting(false)}>
              Back to Editing
            </Button>
          </Stack>

          {testResults && (
            <Paper sx={{ p: 3, bgcolor: 'grey.50' }}>
              <Typography variant="h6" gutterBottom>Test Results</Typography>
              <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                {JSON.stringify(testResults, null, 2)}
              </Typography>
            </Paper>
          )}
        </Box>
      );
    }

    if (viewMode === 'json') {
      return (
        <Box>
          <Typography variant="h6" gutterBottom>JSON Editor</Typography>
          <TextField
            fullWidth
            multiline
            rows={20}
            value={jsonText}
            onChange={(e) => handleJSONChange(e.target.value)}
            error={!!jsonError}
            helperText={jsonError || 'Edit the JSON directly. Changes update in real-time.'}
            sx={{
              fontFamily: 'monospace',
              '& textarea': {
                fontFamily: 'monospace',
                fontSize: '0.9em',
              },
            }}
          />
        </Box>
      );
    }

    // Visual preview mode
    if (!rubric) {
      return (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="textSecondary">
            No rubric loaded
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Create a new rubric or load an existing one to get started
          </Typography>
        </Box>
      );
    }

    // Calculate total max points (handle different field names, exclude bonus questions)
    const totalMaxPoints = rubric.criteria ? Object.entries(rubric.criteria).reduce(
      (sum, [key, criterion]) => {
        // Skip bonus questions
        if (key.toLowerCase() === 'bonus') {
          return sum;
        }
        const crit = criterion as any;
        const maxPoints = criterion.max_points || crit.maxPoints || crit.max_score || 0;
        return sum + maxPoints;
      },
      0
    ) : 0;

    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Visual Preview</Typography>
          <Paper sx={{ px: 2, py: 1, bgcolor: 'primary.light' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'primary.contrastText' }}>
              Total Max Points: {totalMaxPoints}
            </Typography>
          </Paper>
        </Box>
        <Stack spacing={2}>
          {rubric.criteria && Object.entries(rubric.criteria).map(([key, criterion]: [string, Criterion]) => (
            <Paper key={key} sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
                <Chip
                  label={`Question ${key}`}
                  color="primary"
                  size="small"
                  sx={{ fontWeight: 'bold' }}
                />
                <Typography variant="caption" color="textSecondary">
                  Criterion ID: {key}
                </Typography>
              </Box>
              <TextField
                fullWidth
                label="Criterion Name"
                value={criterion.name}
                onChange={(e) => {
                  setRubric(prev => prev ? {
                    ...prev,
                    criteria: {
                      ...prev.criteria,
                      [key]: { ...criterion, name: e.target.value },
                    },
                  } : null);
                }}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Description"
                value={criterion.description || ''}
                onChange={(e) => {
                  setRubric(prev => prev ? {
                    ...prev,
                    criteria: {
                      ...prev.criteria,
                      [key]: { ...criterion, description: e.target.value },
                    },
                  } : null);
                }}
                sx={{ mb: 2 }}
              />
              <TextField
                type="number"
                label="Max Points"
                value={criterion.max_points || (criterion as any).maxPoints || (criterion as any).max_score || 0}
                onChange={(e) => {
                  setRubric(prev => prev ? {
                    ...prev,
                    criteria: {
                      ...prev.criteria,
                      [key]: { ...criterion, max_points: parseInt(e.target.value) || 0 },
                    },
                  } : null);
                }}
                sx={{ width: 150 }}
              />
            </Paper>
          ))}

          {/* Preamble and Postamble */}
          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" gutterBottom>AI Marking Prompts</Typography>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
              Prompt Preamble
            </Typography>
            <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1 }}>
              Sets the AI's role and task. Tells the AI what it is (marking assistant) and what constraints apply.
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={6}
              value={rubric.marking_preamble || ''}
              onChange={(e) => {
                setRubric(prev => prev ? {
                  ...prev,
                  marking_preamble: e.target.value,
                } : null);
              }}
              placeholder="Enter the preamble that sets up the AI marking context..."
            />
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
              Prompt Postamble
            </Typography>
            <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1 }}>
              Defines output format and requirements. Specifies JSON structure and feedback format.
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={8}
              value={rubric.marking_postamble || ''}
              onChange={(e) => {
                setRubric(prev => prev ? {
                  ...prev,
                  marking_postamble: e.target.value,
                } : null);
              }}
              placeholder="Enter the postamble that defines output format and requirements..."
            />
          </Paper>
        </Stack>
      </Box>
    );
  };

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Top Toolbar */}
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Rubric Studio: {rubric?.name || 'Untitled'}
          </Typography>
          <Tooltip title="Test Rubric">
            <IconButton onClick={() => setIsTesting(true)} disabled={!rubric}>
              <ScienceIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export JSON">
            <IconButton onClick={handleExport} disabled={!rubric}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={!rubric || loading}
            sx={{ ml: 2 }}
          >
            Save
          </Button>
        </Toolbar>
      </AppBar>

      {/* Alerts */}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ m: 2 }}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ m: 2 }}>
          {error}
        </Alert>
      )}

      {/* Main workspace */}
      <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
        {/* Left Panel - View Mode & Actions */}
        <Paper sx={{ width: 250, p: 2, borderRadius: 0, overflowY: 'auto' }}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
            View Mode
          </Typography>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, newMode) => newMode && setViewMode(newMode)}
            fullWidth
            sx={{ mb: 3 }}
          >
            <ToggleButton value="visual">
              <ViewModuleIcon fontSize="small" sx={{ mr: 0.5 }} />
              Visual
            </ToggleButton>
            <ToggleButton value="json">
              <CodeIcon fontSize="small" sx={{ mr: 0.5 }} />
              JSON
            </ToggleButton>
          </ToggleButtonGroup>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
            Quick Actions
          </Typography>
          <Stack spacing={1}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              fullWidth
              disabled={!rubric}
              onClick={handleAddCriterion}
            >
              Add Criterion
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AutoFixHighIcon />}
              fullWidth
              disabled={!rubric || isEnhancing}
              onClick={handleAIEnhance}
            >
              {isEnhancing ? 'Enhancing...' : 'AI Enhance'}
            </Button>
          </Stack>
        </Paper>

        {/* Center - Main Workspace */}
        <Box sx={{ flexGrow: 1, p: 3, overflowY: 'auto' }}>
          {renderWorkspace()}
        </Box>

        {/* Right Sidebar - AI Chat */}
        <Paper sx={{ width: 350, borderRadius: 0, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 2 }}>
            <img
              src={markOtterImage}
              alt="Rubric Otter"
              style={{ width: 40, height: 40, borderRadius: '50%' }}
            />
            <Typography variant="h6">Rubric Otter</Typography>
          </Box>

          {/* Chat messages */}
          <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2 }}>
            {chatMessages.length === 0 ? (
              <Typography variant="body2" color="textSecondary" align="center" sx={{ mt: 4 }}>
                Ask Rubric Otter to help refine your rubric
              </Typography>
            ) : (
              <Stack spacing={2}>
                {chatMessages.map((msg, idx) => (
                  <Paper
                    key={idx}
                    sx={{
                      p: 1.5,
                      bgcolor: msg.role === 'user' ? 'primary.main' : msg.role === 'system' ? 'error.light' : 'grey.100',
                      color: msg.role === 'user' ? 'primary.contrastText' : 'text.primary',
                      alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                      maxWidth: '85%',
                    }}
                  >
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {msg.content}
                    </Typography>
                  </Paper>
                ))}
                <div ref={chatEndRef} />
              </Stack>
            )}
          </Box>

          {/* Chat input — isolated component so typing never re-renders the workspace */}
          <ChatInput onSend={handleSendMessage} disabled={isSendingChat || !rubric} />
        </Paper>
      </Box>

      {/* Rubric Selector Dialog */}
      <Dialog open={showRubricSelector} onClose={() => setShowRubricSelector(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Select a Rubric to Refine</DialogTitle>
        <DialogContent>
          <List>
            {availableRubrics.map((r) => (
              <ListItem key={r.id} disablePadding>
                <ListItemButton
                  onClick={() => {
                    setShowRubricSelector(false);
                    loadRubric(r.id);
                  }}
                >
                  <ListItemText
                    primary={r.name}
                    secondary={r.description || 'No description'}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </DialogContent>
      </Dialog>

      {/* Create New Dialog */}
      <Dialog open={showCreateDialog} onClose={() => setShowCreateDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Create New Rubric</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {/* Step 1: Select Rubric Guide Type */}
            <Paper sx={{ p: 3, bgcolor: 'grey.50' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Chip label="Step 1" color="primary" />
                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                  Select Rubric Guide Type
                </Typography>
                <Button
                  size="small"
                  startIcon={<InfoIcon />}
                  onClick={() => setShowGuideTypesModal(true)}
                >
                  Compare All Types
                </Button>
              </Box>

            <Stack spacing={1.5}>
              {RUBRIC_GUIDE_TYPES.map((guideType) => (
                <Paper
                  key={guideType.value}
                  sx={{
                    p: 1.5,
                    cursor: 'pointer',
                    border: selectedGuideType === guideType.value ? 2 : 1,
                    borderColor: selectedGuideType === guideType.value ? 'primary.main' : 'divider',
                    bgcolor: selectedGuideType === guideType.value ? 'primary.50' : 'white',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                  onClick={() => setSelectedGuideType(guideType.value)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <input
                      type="radio"
                      checked={selectedGuideType === guideType.value}
                      onChange={() => setSelectedGuideType(guideType.value)}
                      style={{ marginTop: 4 }}
                    />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {guideType.label}
                        {guideType.recommended && (
                          <Chip label="Recommended" size="small" color="primary" sx={{ ml: 1, height: 20 }} />
                        )}
                      </Typography>
                      <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.5 }}>
                        {guideType.description}
                      </Typography>
                      <Typography variant="caption" color="primary" sx={{ mt: 0.5, display: 'block' }}>
                        Best for: {guideType.bestFor.join(', ')}
                      </Typography>
                    </Box>
                    <Tooltip title="View details">
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); setShowGuideTypesModal(true); }}>
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Paper>
              ))}
              </Stack>
            </Paper>

            {/* Step 2: Review/Edit Example Template */}
            <Paper sx={{ p: 3, bgcolor: 'info.50' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Chip label="Step 2" color="primary" />
                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                  Review/Edit Example Template
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    const guideType = RUBRIC_GUIDE_TYPES.find(type => type.value === selectedGuideType);
                    if (guideType?.exampleRubric) {
                      setExampleRubric(guideType.exampleRubric);
                    }
                  }}
                >
                  Reset to Default
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={() => setExampleRubric('')}
                >
                  Clear Example
                </Button>
              </Box>

              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  The AI will use this example to match your rubric's style, format, and detail level.
                  Edit it to match your preferences, or leave as-is.
                </Typography>
              </Alert>

              {exampleRubric ? (
                <TextField
                  fullWidth
                  multiline
                  rows={12}
                  value={exampleRubric}
                  onChange={(e) => setExampleRubric(e.target.value)}
                  placeholder="Example rubric template in JSON format..."
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.85rem',
                    '& textarea': {
                      fontFamily: 'monospace',
                    },
                    bgcolor: 'white'
                  }}
                />
              ) : (
                <Alert severity="warning">
                  No default example for this guide type. You can paste your own example here, or proceed without one.
                </Alert>
              )}
            </Paper>

            {/* Step 3: Provide Your Assessment */}
            <Paper sx={{ p: 3, bgcolor: 'grey.50' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Chip label="Step 3" color="primary" />
                <Typography variant="h6">
                  Provide Your Assessment
                </Typography>
              </Box>

              <ToggleButtonGroup
                value={createMethod}
                exclusive
                onChange={(_, value) => value && setCreateMethod(value)}
                fullWidth
                sx={{ mb: 3 }}
              >
                <ToggleButton value="upload">Upload File</ToggleButton>
                <ToggleButton value="paste">Paste Documents</ToggleButton>
                <ToggleButton value="describe">Describe</ToggleButton>
              </ToggleButtonGroup>

          {createMethod === 'upload' && (
            <Stack spacing={2}>
              <Typography variant="body2" color="textSecondary">
                Upload a PDF, image, or text file containing your marking guidelines. AI will extract and structure the rubric.
              </Typography>
              <TextField
                fullWidth
                label="Rubric Name"
                value={rubricName}
                onChange={(e) => setRubricName(e.target.value)}
                placeholder="e.g., Gr9 IT Exam JS/HTML"
                required
                helperText="Give your rubric a clear, descriptive name"
              />
              <input
                type="file"
                id="create-upload"
                accept=".pdf,.png,.jpg,.jpeg,.txt"
                onChange={(e) => e.target.files && setUploadedFile(e.target.files[0])}
                style={{ display: 'none' }}
              />
              <label htmlFor="create-upload">
                <Button variant="outlined" component="span" fullWidth>
                  {uploadedFile ? uploadedFile.name : 'Choose File'}
                </Button>
              </label>
              <TextField
                fullWidth
                label="Description (Optional)"
                value={rubricDescription}
                onChange={(e) => setRubricDescription(e.target.value)}
                multiline
                rows={2}
              />
              <TextField
                fullWidth
                label="Additional Instructions for AI (Optional)"
                value={additionalInstructions}
                onChange={(e) => setAdditionalInstructions(e.target.value)}
                multiline
                rows={3}
                placeholder='e.g., "Include the bonus section at the end", "Use ranges for partial marks (1-2, 3-4)", "Focus on common coding mistakes"'
                helperText="Provide any special instructions to guide the AI in generating your rubric"
              />
              <Button
                variant="contained"
                onClick={handleCreateFromUpload}
                disabled={!uploadedFile || loading}
                startIcon={loading ? <CircularProgress size={20} /> : null}
              >
                {loading ? 'Creating...' : 'Create Rubric'}
              </Button>
            </Stack>
          )}

          {createMethod === 'paste' && (
            <Stack spacing={2}>
              <Typography variant="body2" color="textSecondary">
                Paste your question paper and memorandum. AI will create a structured rubric.
              </Typography>
              <TextField
                fullWidth
                label="Rubric Name"
                value={rubricName}
                onChange={(e) => setRubricName(e.target.value)}
                placeholder="e.g., Gr9 IT Exam JS/HTML"
                required
                helperText="Give your rubric a clear, descriptive name"
              />
              <TextField
                fullWidth
                label="Question Paper"
                value={questionPaper}
                onChange={(e) => setQuestionPaper(e.target.value)}
                multiline
                rows={6}
              />
              <TextField
                fullWidth
                label="Memorandum / Marking Guide"
                value={memorandum}
                onChange={(e) => setMemorandum(e.target.value)}
                multiline
                rows={6}
              />
              <TextField
                fullWidth
                label="Description (Optional)"
                value={rubricDescription}
                onChange={(e) => setRubricDescription(e.target.value)}
                multiline
                rows={2}
              />
              <TextField
                fullWidth
                label="Additional Instructions for AI (Optional)"
                value={additionalInstructions}
                onChange={(e) => setAdditionalInstructions(e.target.value)}
                multiline
                rows={3}
                placeholder='e.g., "Include the bonus section at the end", "Use ranges for partial marks (1-2, 3-4)", "Focus on common coding mistakes"'
                helperText="Provide any special instructions to guide the AI in generating your rubric"
              />
              <Button
                variant="contained"
                onClick={handleCreateFromPaste}
                disabled={!questionPaper.trim() || !memorandum.trim() || loading}
                startIcon={loading ? <CircularProgress size={20} /> : null}
              >
                {loading ? 'Creating...' : 'Create Rubric'}
              </Button>
            </Stack>
          )}

          {createMethod === 'describe' && (
            <Stack spacing={2}>
              <Typography variant="body2" color="textSecondary">
                Describe your rubric requirements. AI will create a structured rubric based on your description.
              </Typography>
              <TextField
                fullWidth
                label="Rubric Name"
                value={rubricName}
                onChange={(e) => setRubricName(e.target.value)}
                placeholder="e.g., Gr9 IT Exam JS/HTML"
                required
                helperText="Give your rubric a clear, descriptive name"
              />
              <TextField
                fullWidth
                label="Rubric Description"
                placeholder="E.g., 'Create a rubric for grading Grade 10 Python programs. Include criteria for code quality, comments, functionality, and user experience. Total points should be 50.'"
                value={rubricDescription}
                onChange={(e) => setRubricDescription(e.target.value)}
                multiline
                rows={8}
              />
              <TextField
                fullWidth
                label="Additional Instructions for AI (Optional)"
                value={additionalInstructions}
                onChange={(e) => setAdditionalInstructions(e.target.value)}
                multiline
                rows={3}
                placeholder='e.g., "Include the bonus section at the end", "Use ranges for partial marks (1-2, 3-4)", "Focus on common coding mistakes"'
                helperText="Provide any special instructions to guide the AI in generating your rubric"
              />
              <Button
                variant="contained"
                onClick={handleCreateFromDescription}
                disabled={!rubricDescription.trim() || loading}
                startIcon={loading ? <CircularProgress size={20} /> : null}
              >
                {loading ? 'Creating...' : 'Create Rubric'}
              </Button>
            </Stack>
          )}
            </Paper>
          </Stack>
        </DialogContent>
      </Dialog>

      {/* Import JSON Dialog */}
      <Dialog open={showImportDialog} onClose={() => setShowImportDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Import Rubric from JSON</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="textSecondary">
              Paste your rubric JSON below. You can also upload a .json file.
            </Typography>
            <TextField
              fullWidth
              label="Rubric JSON"
              placeholder='{"criterion_1": {"name": "...", "description": "...", "max_points": 10}, ...}'
              value={importJsonText}
              onChange={(e) => {
                setImportJsonText(e.target.value);
                setImportJsonError(null);
              }}
              multiline
              rows={12}
              error={!!importJsonError}
              helperText={importJsonError}
              sx={{
                fontFamily: 'monospace',
                '& textarea': {
                  fontFamily: 'monospace',
                  fontSize: '0.9em',
                },
              }}
            />
            <Box>
              <input
                type="file"
                id="import-json-file"
                accept=".json"
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    const file = e.target.files[0];
                    const reader = new FileReader();
                    reader.onload = (event) => {
                      setImportJsonText(event.target?.result as string || '');
                    };
                    reader.readAsText(file);
                  }
                }}
                style={{ display: 'none' }}
              />
              <label htmlFor="import-json-file">
                <Button variant="outlined" component="span">
                  Or Upload JSON File
                </Button>
              </label>
            </Box>
            <Button
              variant="contained"
              onClick={handleImportJson}
              disabled={!importJsonText.trim()}
            >
              Import Rubric
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      {/* Compare Guide Types Modal */}
      <Dialog open={showGuideTypesModal} onClose={() => setShowGuideTypesModal(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Compare Rubric Guide Types</Typography>
            <IconButton onClick={() => setShowGuideTypesModal(false)}>
              <InfoIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" paragraph>
            Choose the guide type that best matches your assessment needs. Each type provides different levels
            of detail and structure for marking guidance.
          </Typography>

          <Stack spacing={3}>
            {RUBRIC_GUIDE_TYPES.map((guideType) => (
              <Paper key={guideType.value} sx={{ p: 3, border: guideType.recommended ? 2 : 1, borderColor: guideType.recommended ? 'primary.main' : 'divider' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      {guideType.label}
                      {guideType.recommended && (
                        <Chip label="Recommended" size="small" color="primary" sx={{ ml: 2 }} />
                      )}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" paragraph>
                      {guideType.description}
                    </Typography>
                  </Box>
                  <Button
                    variant={selectedGuideType === guideType.value ? 'contained' : 'outlined'}
                    size="small"
                    onClick={() => {
                      setSelectedGuideType(guideType.value);
                      setShowGuideTypesModal(false);
                    }}
                  >
                    {selectedGuideType === guideType.value ? 'Selected' : 'Select'}
                  </Button>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                      Best For:
                    </Typography>
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      {guideType.bestFor.map((item, idx) => (
                        <li key={idx}>
                          <Typography variant="body2">{item}</Typography>
                        </li>
                      ))}
                    </ul>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                      Includes:
                    </Typography>
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      {guideType.includes.map((item, idx) => (
                        <li key={idx}>
                          <Typography variant="body2">{item}</Typography>
                        </li>
                      ))}
                    </ul>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Stack>
        </DialogContent>
      </Dialog>

    </Box>
  );
};

export default RubricStudio;
